"use client"

import { PaginationType } from "@/app/types/pagination";
import { Box, Button, colors, Grid2 as Grid, LinearProgress } from "@mui/material";
import CenterBox from "@v2/components/styled/CenterBox";
import FlexBox from "@v2/components/styled/FlexBox";
import { MonitorDown } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Fragment, MouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getProductListClient } from "../clientApi";
import { readLoadedPages, saveLoadedPages } from "../plpLoadedPagesCache";
import { buildPageHref } from "../paginationHref";
import { getPlpLoadedPages, PLP_DEFAULT_LIMIT } from "../productListParams";
import { PLPSearchParams, PLPSiteProduct } from "../types";
import { buildItem, trackViewItemList } from "@v2/lib/analytics";
import PlpSiteProduct from "./PlpSiteProduct";

function syncLoadedPagesInUrl(loadedPages: number) {
    const url = new URL(window.location.href);
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    url.searchParams.delete("page");
    url.searchParams.delete("limit");
    if (loadedPages > 1) {
        url.searchParams.set("lp", String(loadedPages));
    } else {
        url.searchParams.delete("lp");
    }
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(null, "", nextUrl);
    window.requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
}

function getLoadedPagesFromSearchParams(searchParams: URLSearchParams) {
    const loadedPages = Number.parseInt(searchParams.get("lp") ?? "1", 10);
    return Number.isFinite(loadedPages) && loadedPages > 1 ? loadedPages : 1;
}

// TypeSense caps per_page at 250, so one request reaches 13 pages of 18 at most.
const PLP_MAX_PAGES_PER_REQUEST = 13;

// Fetches the pages between the ones already on screen and the one the shopper
// had open, as a single read of the list wherever it fits. Separate per-page
// requests can overlap or skip rows when the list shifts underneath them, and
// firing them all at once puts a burst on the search backend for nothing.
async function fetchPageRange(searchParams: PLPSearchParams, fromPage: number, toPage: number): Promise<PLPSiteProduct[]> {
    const skipCount = PLP_DEFAULT_LIMIT * (fromPage - 1);

    if (toPage <= PLP_MAX_PAGES_PER_REQUEST) {
        const response = await getProductListClient({
            ...searchParams,
            page: "1",
            limit: String(PLP_DEFAULT_LIMIT * toPage),
            lp: undefined,
        }, true);

        return response.data.slice(skipCount);
    }

    // Past the cap the rest is walked one page at a time, in order.
    const collected: PLPSiteProduct[] = [];
    for (let pageNumber = fromPage; pageNumber <= toPage; pageNumber++) {
        const response = await getProductListClient({
            ...searchParams,
            page: String(pageNumber),
            limit: String(PLP_DEFAULT_LIMIT),
            lp: undefined,
        }, true);

        collected.push(...response.data);
    }

    return collected;
}

export default function PlpLoadMore({ pagination, searchParams, visibleSearchParams, initialProductCount, listId, listName, showProductTitle = false, showBrand = false, rounded = false }: {
    pagination: PaginationType,
    searchParams: PLPSearchParams,
    visibleSearchParams?: PLPSearchParams,
    initialProductCount: number,
    listId?: string,
    listName?: string,
    showProductTitle?: boolean,
    showBrand?: boolean,
    rounded?: boolean,
}) {
    const loadedPages = getPlpLoadedPages(searchParams);
    const serverPage = Math.max(1, pagination.current_page || 1);
    const renderedInitialPages = Math.max(1, Math.ceil(initialProductCount / PLP_DEFAULT_LIMIT));
    // With lp the server merges pages 1..N, so never claim more pages than are rendered.
    // A direct ?page=N entry renders only page N, and N is the real position in the list.
    const initialPage = loadedPages > 1 ? Math.min(serverPage, renderedInitialPages) : serverPage;
    const firstPage = loadedPages > 1 ? 1 : serverPage;
    const [loading, setLoading] = useState(false)
    const [siteProducts, setSiteProducts] = useState<PLPSiteProduct[]>([])
    const [page, setPage] = useState(initialPage)
    const [show, setShow] = useState(pagination.last_page > initialPage)
    const seededFromSnapshot = useRef(false)

    const pathname = usePathname()

    // Coming back from a product page the extra pages are gone from state, and the
    // router replays the payload this entry was first rendered with — page one on
    // its own. Putting the snapshot back before the browser paints keeps the list
    // whole with no second round trip, so the scroll restore finds the card it is
    // looking for straight away.
    // This is a layout effect rather than a lazy initial state on purpose: a direct
    // ?lp=N entry gets those pages from the server, and seeding during render would
    // not match the markup being hydrated.
    useLayoutEffect(() => {
        const targetPage = Math.min(getLoadedPagesFromSearchParams(new URLSearchParams(window.location.search)), pagination.last_page);
        const snapshot = readLoadedPages(initialProductCount, targetPage);
        if (!snapshot) return;

        seededFromSnapshot.current = true;
        setSiteProducts(snapshot.products)
        setPage(snapshot.page)
    }, [initialProductCount, pagination.last_page])

    useEffect(() => {
        setShow(pagination.last_page > page)
    }, [page, pagination.last_page])

    useEffect(() => {
        // The snapshot already covers every page up to lp.
        if (seededFromSnapshot.current) return;

        const targetPage = Math.min(getLoadedPagesFromSearchParams(new URLSearchParams(window.location.search)), pagination.last_page);
        if (targetPage <= page) return;

        let cancelled = false;
        setLoading(true);

        fetchPageRange(searchParams, page + 1, targetPage).then((products) => {
            if (cancelled) return;

            setSiteProducts(currentProducts => [...currentProducts, ...products])
            setPage(targetPage)
        }).finally(() => {
            if (!cancelled) setLoading(false)
        })

        return () => {
            cancelled = true;
        }
    }, [page, pagination.last_page, searchParams])

    // Written after the url carries the new lp, so the snapshot is filed under the
    // address the back button will return to.
    useEffect(() => {
        saveLoadedPages(page, initialProductCount, siteProducts)
    }, [siteProducts, page, initialProductCount])

    const getMoreData = useCallback(() => {
        setLoading(true)
        const params: PLPSearchParams = {
            ...searchParams,
            page: String(page + 1),
            limit: String(PLP_DEFAULT_LIMIT),
            lp: undefined,
        }

        getProductListClient(params, true).then((response) => {
            if (!response) return;

            setSiteProducts(currentProducts => [...currentProducts, ...response.data])
            setPage(response.pagination.current_page)
            setShow(response.pagination.last_page > response.pagination.current_page)
            syncLoadedPagesInUrl(response.pagination.current_page)
        }).finally(() => {
            setLoading(false)
        })
    }, [page, searchParams])

    // The href a crawler follows, and the address a modifier click opens.
    const nextPageHref = buildPageHref(pathname, visibleSearchParams ?? searchParams, page + 1);

    const loadNextPage = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
        // A modifier click means the shopper wants that page on its own, so the
        // browser is left to follow the href.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        if (loading) return;

        getMoreData();
    }, [getMoreData, loading])

    // Pages after the first arrive because the shopper asked for them, so they
    // are reported as soon as they render — no visibility check needed, the
    // shopper is already down there looking. Only the new rows go out, and the
    // index carries on from where the first page stopped.
    const reportedCount = useRef(0)
    const analyticsItem = useCallback((siteProduct: PLPSiteProduct, index: number) => {
        const variant = siteProduct.details?.[0]

        return buildItem({
            variantId: variant?.product_id ?? siteProduct.id,
            name: variant?.name ?? siteProduct.title,
            brand: siteProduct.brand?.en_name,
            tomanPrice: variant?.price,
            tomanDiscount: variant?.discount,
            siteProductId: siteProduct.id,
            index,
            listId,
            listName,
        })
    }, [listId, listName])

    useEffect(() => {
        if (!listId || !listName) return;
        if (siteProducts.length <= reportedCount.current) return;

        const offset = reportedCount.current
        const fresh = siteProducts.slice(offset)
        reportedCount.current = siteProducts.length

        trackViewItemList(
            fresh.map((siteProduct, index) => analyticsItem(siteProduct, initialProductCount + offset + index)),
            listId,
            listName,
        )
    }, [siteProducts, listId, listName, initialProductCount, analyticsItem])

    const showFrom = pagination.total ? (firstPage - 1) * PLP_DEFAULT_LIMIT + 1 : 0;
    const currentToByPerPage = PLP_DEFAULT_LIMIT * page;
    const currentTo = currentToByPerPage > pagination.total ? pagination.total : currentToByPerPage;
    const percent = pagination.total ? (((currentTo - showFrom + 1) * 100) / pagination.total) : 0

    return (
        <Fragment>
            {siteProducts.map((siteProduct, index) => (
                <PlpSiteProduct
                    showProductTitle={showProductTitle}
                    showBrand={showBrand}
                    rounded={rounded}
                    key={siteProduct.id}
                    siteProduct={siteProduct}
                    listId={listId}
                    listName={listName}
                    listIndex={initialProductCount + index}
                />
            ))}

            {/* this component is loaded into a <Grid container .... */}
            <Grid size={12} sx={{
                py: 4,
            }}>
                <CenterBox sx={{ flexDirection: 'column' }}>

                    <Box sx={{
                        width: '100%', maxWidth: {
                            xs: 290,
                            sm: 370
                        }
                    }}>
                        <FlexBox sx={{
                            justifyContent: 'center',
                            fontSize: 14,
                            color: colors.grey[700],
                            fontWeight: 300,
                            gap: '4px',
                            '& b': {
                                fontWeight: 500,
                                color: colors.grey[900],

                            }
                        }}
                        //  className="en-num"
                        >
                            <span className="">در حال مشاهده</span>
                            <span className=""><b>{showFrom}</b> تا <b>{currentTo}</b></span>
                            <span className="">از <b>{pagination.total}</b> مورد</span>
                        </FlexBox>
                        <Box dir="rtl">
                            <LinearProgress color="inherit" variant="determinate"
                                aria-label="products loaded process bar"
                                value={percent}
                                sx={{
                                    borderRadius: 1, mt: 1, transform: 'rotateY(180deg)'
                                }} />
                        </Box>
                    </Box>
                    {show && (
                        // The button is the crawlable path into the rest of the list: a real
                        // link to the next page, with the label as its anchor text, that the
                        // click handler turns into an inline load. A modifier click is left
                        // alone so opening it in a new tab still lands on that page.
                        <Button variant="outlined" color="inherit"
                            component={Link}
                            href={nextPageHref}
                            prefetch={false}
                            sx={{
                                height: 48,
                                width: {
                                    xs: 240,
                                    sm: 380
                                },
                                mt: {
                                    xs: 3,
                                    sm: 2
                                },
                            }} endIcon={
                                !loading
                                    ? <MonitorDown />
                                    : <Fragment />
                        } onClick={loadNextPage} loading={loading}>
                            نمایش محصولات بیشتر
                        </Button>
                    )}

                </CenterBox>
            </Grid>

        </Fragment>
    )
}
